import { SUPPORT_EMAIL } from './support';

export type DemoQuotaKind = 'photo' | 'video';

type DemoUsageState = {
  photo: number;
  video: number;
};

const DEMO_USAGE_KEY = 'fabrica.demoUsage.v1';
const DEMO_BADGE_ID = 'demo-mode-badge';
const DEMO_WIDGET_ID = 'demo-mode-widget';
const DEMO_DROPDOWN_ID = 'user-dropdown';

const DEFAULT_USAGE: DemoUsageState = {
  photo: 0,
  video: 0
};

const DEMO_LIMITS: Record<DemoQuotaKind, number> = {
  photo: 10,
  video: 3
};

export function isDemoMode(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path === '/demo';
}

function readDemoUsage(): DemoUsageState {
  try {
    const raw = localStorage.getItem(DEMO_USAGE_KEY);
    if (!raw) return { ...DEFAULT_USAGE };
    const parsed = JSON.parse(raw) as Partial<DemoUsageState> | null;
    return {
      photo: Number.isFinite(parsed?.photo) ? Math.max(0, Number(parsed?.photo)) : 0,
      video: Number.isFinite(parsed?.video) ? Math.max(0, Number(parsed?.video)) : 0
    };
  } catch {
    return { ...DEFAULT_USAGE };
  }
}

function writeDemoUsage(state: DemoUsageState): void {
  try {
    localStorage.setItem(DEMO_USAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors in demo mode.
  }
}

export function getDemoQuota(kind: DemoQuotaKind): { used: number; limit: number; remaining: number } {
  const usage = readDemoUsage();
  const used = usage[kind] || 0;
  const limit = DEMO_LIMITS[kind];
  return {
    used,
    limit,
    remaining: Math.max(0, limit - used)
  };
}

export function canUseDemoQuota(kind: DemoQuotaKind, amount: number = 1): boolean {
  const { remaining } = getDemoQuota(kind);
  return remaining >= amount;
}

export function consumeDemoQuota(kind: DemoQuotaKind, amount: number = 1): boolean {
  if (!canUseDemoQuota(kind, amount)) return false;
  const usage = readDemoUsage();
  usage[kind] = Math.max(0, (usage[kind] || 0) + amount);
  writeDemoUsage(usage);
  return true;
}

export function getDemoLimitMessage(kind: DemoQuotaKind): string {
  const quota = getDemoQuota(kind);
  const noun = kind === 'photo' ? 'фото' : 'видео';
  if (quota.remaining <= 0) {
    return `Лимит демо на сохранение ${noun} исчерпан`;
  }
  return `Демо: осталось ${quota.remaining} из ${quota.limit} сохранений ${noun}`;
}

export function getDemoRestrictionMessage(action: 'save-project' | 'load-project' | 'new-project' | 'load-model'): string {
  if (action === 'save-project') return 'В демо-режиме сохранение проекта недоступно';
  if (action === 'load-project') return 'В демо-режиме открытие проекта недоступно';
  if (action === 'load-model') return 'В демо-режиме открытие модели недоступно';
  return 'В демо-режиме создание нового проекта недоступно';
}

export function ensureDemoBadge(): void {
  if (!isDemoMode()) return;
  if (document.getElementById(DEMO_WIDGET_ID)) return;

  const style = document.createElement('style');
  style.id = `${DEMO_BADGE_ID}-styles`;
  /* 233px = ширина правого сайдбара; центр канваса = (100vw - 233px) / 2 */
  style.textContent = `
    #${DEMO_WIDGET_ID} {
      position: fixed;
      top: 8px;
      left: calc((100vw - 233px) / 2);
      transform: translateX(-50%);
      z-index: 1000;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    }
    #${DEMO_BADGE_ID} {
      background: #d9f903;
      color: #000000;
      border-radius: 9px;
      padding: 6px 12px;
      font-size: 13px;
      font-weight: 600;
      box-shadow: 0 4px 12px rgba(0,0,0,0.25);
      cursor: pointer;
      user-select: none;
      white-space: nowrap;
    }
    #${DEMO_DROPDOWN_ID} {
      position: absolute;
      top: calc(100% + 8px);
      left: 50%;
      transform: translate(-50%, -8px);
      width: min(380px, calc(100vw - 24px));
      background: #1a1a1a;
      border-radius: 12px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.5);
      opacity: 0;
      visibility: hidden;
      transition: all 0.2s ease;
      overflow: hidden;
    }
    #${DEMO_DROPDOWN_ID}.visible {
      opacity: 1;
      visibility: visible;
      transform: translate(-50%, 0);
      width: 300px;
    }
    #${DEMO_DROPDOWN_ID} .user-dropdown-header {
      background: #d9f90300;
      color: #000000;
      padding: 16px;
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }
    #${DEMO_DROPDOWN_ID} .login-icon {
      flex-shrink: 0;
      width: 44px;
      height: 44px;
      border-radius: 10px;
      background: rgba(0,0,0,0.08);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    #${DEMO_DROPDOWN_ID} .user-email {
      color: #ffffff;
      font-size: 15px;
      font-weight: 600;
      margin-bottom: 6px;
    }
    #${DEMO_DROPDOWN_ID} .user-expires {
      color: rgb(255 255 255);
      font-size: 11px;
      line-height: 1.2;
    }
    #${DEMO_DROPDOWN_ID} .user-expires + .user-expires {
      margin-top: 8px;
    }
    #${DEMO_DROPDOWN_ID} .user-dropdown-menu {
      padding: 10px;
    }
    #${DEMO_DROPDOWN_ID} .user-menu-item {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 40px;
      border-radius: 8px;
      background: #ffffff;
      color: #000000;
      font-size: 13px;
      font-weight: 600;
      text-decoration: none;
      transition: background 0.15s ease;
    }
    #${DEMO_DROPDOWN_ID} .user-menu-item:hover {
      background: #f4f4f4;
    }
  `;
  document.head.appendChild(style);

  const widget = document.createElement('div');
  widget.id = DEMO_WIDGET_ID;
  widget.innerHTML = `
    <div id="${DEMO_BADGE_ID}">Демо-режим</div>
    <div class="user-dropdown" id="${DEMO_DROPDOWN_ID}">
      <div class="user-dropdown-header">
        <div class="login-icon" aria-hidden="true">
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="43.4102" height="43.4102" rx="8.4858" fill="#D9F903"/>
            <path d="M35.0976 20.0403L25.4442 22.5577L33.5873 36.0852L26.8189 36.0372L22.1114 27.5925L17.4409 35.9708L10.7292 35.9233L18.0053 22.5577L8.3125 20.0403V16.6237H35.0981L35.0976 20.0403Z" fill="black"/>
            <path d="M25.4104 13.1377C24.7126 13.7746 23.4915 14.0931 21.7469 14.0931C20.0024 14.0931 18.7987 13.7746 18.1358 13.1377C17.438 12.4689 17.0891 11.6726 17.0891 10.749V9.36363C17.0891 8.40817 17.438 7.61196 18.1358 6.97499C18.7987 6.33802 20.0024 6.01953 21.7469 6.01953C23.4915 6.01953 24.7126 6.33802 25.4104 6.97499C26.0733 7.61196 26.4048 8.40817 26.4048 9.36363V10.749C26.4048 11.6726 26.0733 12.4689 25.4104 13.1377Z" fill="black"/>
          </svg>
        </div>
        <div>
          <div class="user-email">Фабрика Уно</div>
          <div class="user-expires">3D-мокапы для согласования дизайна. Покажите клиенту готовый продукт до старта производства</div>
          <div class="user-expires">Покажем, как будет выглядеть ваш мокап, и предложим формат работы под ваш объём заказов</div>
        </div>
      </div>
      <div class="user-dropdown-menu">
        <button type="button" class="user-menu-item" id="demo-support-btn">Связаться с нами</button>
      </div>
    </div>
  `;
  document.body.appendChild(widget);

  const badge = document.getElementById(DEMO_BADGE_ID);
  const dropdown = document.getElementById(DEMO_DROPDOWN_ID);
  const supportBtn = document.getElementById('demo-support-btn');
  badge?.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown?.classList.toggle('visible');
  });
  supportBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown?.classList.remove('visible');
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  });
  dropdown?.addEventListener('click', (e) => {
    e.stopPropagation();
  });
  document.addEventListener('click', () => {
    dropdown?.classList.remove('visible');
  });
}
